import type { IconName } from './icons';

/**
 * خريطة الشاشات: اسم كل شاشة وسطر يشرحها ومجموعتها فى القائمة وأيقونتها. القائمة وعنوان
 * الصفحة وعنوان تبويب المتصفح كلها تُقرأ من هنا، فلا يختلف اسم الشاشة بين موضعين.
 * الترتيب داخل المجموعة هو ترتيب الظهور فى القائمة.
 */
export type ViewKind =
  | 'dashboard'
  | 'journal'
  | 'ledger'
  | 'periods'
  | 'reports'
  | 'reportsCenter'
  | 'customers'
  | 'invoices'
  | 'catalog'
  | 'inventory'
  | 'receipts'
  | 'suppliers'
  | 'bills'
  | 'payments'
  | 'treasury'
  | 'expenses'
  | 'treasuryAccounts'
  | 'reconciliation'
  | 'settings'
  | 'setup'
  | 'migration'
  | 'platform';

export type NavGroup =
  'home' | 'sales' | 'purchases' | 'stock' | 'cash' | 'accounting' | 'reports' | 'admin';

export interface ScreenMeta {
  title: string;
  hint: string;
  group: NavGroup;
  icon: IconName;
}

/** عناوين المجموعات بالترتيب. `home` و`admin` بلا عنوان ظاهر — بند واحد أعلى القائمة وأسفلها. */
export const NAV_GROUPS: readonly { id: NavGroup; title: string | null }[] = [
  { id: 'home', title: null },
  { id: 'sales', title: 'المبيعات' },
  { id: 'purchases', title: 'المشتريات' },
  { id: 'stock', title: 'الأصناف والمخزون' },
  { id: 'cash', title: 'الخزينة والبنوك' },
  { id: 'accounting', title: 'المحاسبة' },
  { id: 'reports', title: 'التقارير' },
  { id: 'admin', title: null },
];

export const SCREENS: Record<ViewKind, ScreenMeta> = {
  platform: {
    title: 'لوحة المنصة',
    hint: 'الشركات المشتركة وخططها وحالتها.',
    group: 'home',
    icon: 'platform',
  },
  dashboard: {
    title: 'الرئيسية',
    hint: 'مؤشرات الفترة وتنبيهاتها ومهام اليوم.',
    group: 'home',
    icon: 'home',
  },
  customers: {
    title: 'العملاء',
    hint: 'بيانات العملاء وأرصدتهم وكشوف حساباتهم.',
    group: 'sales',
    icon: 'users',
  },
  invoices: {
    title: 'فواتير المبيعات',
    hint: 'إصدار الفواتير ومتابعة المتبقى منها، والإشعارات الدائنة والمدينة.',
    group: 'sales',
    icon: 'invoice',
  },
  receipts: {
    title: 'التحصيل',
    hint: 'تسجيل ما يُقبض من العملاء وتخصيصه على فواتيرهم.',
    group: 'sales',
    icon: 'receive',
  },
  suppliers: {
    title: 'الموردون',
    hint: 'بيانات الموردين وأرصدتهم وكشوف حساباتهم.',
    group: 'purchases',
    icon: 'truck',
  },
  bills: {
    title: 'فواتير الموردين',
    hint: 'تسجيل المشتريات والخدمات الواردة من الموردين.',
    group: 'purchases',
    icon: 'bill',
  },
  payments: {
    title: 'السداد للموردين',
    hint: 'ما يُدفع للموردين وتخصيصه على فواتيرهم.',
    group: 'purchases',
    icon: 'send',
  },
  catalog: {
    title: 'الأصناف والخدمات',
    hint: 'ما تبيعه وتشتريه بأسعاره وحساباته.',
    group: 'stock',
    icon: 'tag',
  },
  inventory: {
    title: 'المخزون',
    hint: 'الكميات والتكلفة والحركات والجرد.',
    group: 'stock',
    icon: 'box',
  },
  treasury: {
    title: 'الخزينة',
    hint: 'حركات القبض والصرف والتحويل بين الخزائن والبنوك.',
    group: 'cash',
    icon: 'wallet',
  },
  expenses: {
    title: 'المصروفات النقدية',
    hint: 'مصروفات تُدفع نقدًا أو من البنك مباشرة.',
    group: 'cash',
    icon: 'expense',
  },
  treasuryAccounts: {
    title: 'الخزائن والبنوك',
    hint: 'تعريف الخزائن والحسابات البنكية وأرصدتها.',
    group: 'cash',
    icon: 'bank',
  },
  reconciliation: {
    title: 'المطابقة البنكية',
    hint: 'مطابقة كشف البنك مع الدفاتر.',
    group: 'cash',
    icon: 'reconcile',
  },
  journal: {
    title: 'قيد يومية',
    hint: 'قيود يدوية وقيود الافتتاح.',
    group: 'accounting',
    icon: 'journal',
  },
  ledger: {
    title: 'كشف حساب',
    hint: 'حركة أى حساب فى دليل الحسابات ورصيده.',
    group: 'accounting',
    icon: 'ledger',
  },
  periods: {
    title: 'الفترات المالية',
    hint: 'فتح الفترات وإقفالها وإعادة فتحها.',
    group: 'accounting',
    icon: 'calendar',
  },
  reports: {
    title: 'القوائم المالية',
    hint: 'قائمة الدخل والميزانية والأعمار والقيمة المضافة.',
    group: 'reports',
    icon: 'chart',
  },
  reportsCenter: {
    title: 'مركز التقارير',
    hint: 'كل التقارير التحليلية والسجلات فى مكان واحد.',
    group: 'reports',
    icon: 'reports',
  },
  setup: {
    title: 'إعداد الشركة',
    hint: 'البيانات والهوية والسنة المالية والأرصدة الافتتاحية والمستخدمون — خطوة بخطوة.',
    group: 'admin',
    icon: 'sparkle',
  },
  migration: {
    title: 'ترحيل البيانات',
    hint: 'انقل بياناتك من برنامج سابق: الدليل والأرصدة والعملاء والموردين والأصناف — بمراجعة قبل الاعتماد.',
    group: 'admin',
    icon: 'refresh',
  },
  settings: {
    title: 'الإعدادات',
    hint: 'بيانات الشركة وخياراتها والمستخدمون والفاتورة الإلكترونية.',
    group: 'admin',
    icon: 'settings',
  },
};

/** الشاشات بمجموعاتها بترتيب القائمة، بعد استبعاد ما لا يُعرض لهذا المستخدم. */
export function navSections(
  visible: (kind: ViewKind) => boolean,
): { id: NavGroup; title: string | null; items: ViewKind[] }[] {
  const kinds = Object.keys(SCREENS) as ViewKind[];
  return NAV_GROUPS.map((group) => ({
    ...group,
    items: kinds.filter((kind) => SCREENS[kind].group === group.id && visible(kind)),
  })).filter((group) => group.items.length > 0);
}

/** عنوان تبويب المتصفح: الشاشة ثم الشركة ثم المنتج — الأهم أولاً حين يُقصّ العنوان. */
export function documentTitle(kind: ViewKind, companyName: string | null): string {
  return [SCREENS[kind].title, companyName, 'حسابات نواة'].filter(Boolean).join(' — ');
}

/** الحرفان الأولان من الاسم لشارة المستخدم — «أحمد سامى» ← «أس». */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '؟';
  const first = Array.from(parts[0] ?? '')[0] ?? '';
  const last = parts.length > 1 ? (Array.from(parts[parts.length - 1] ?? '')[0] ?? '') : '';
  return first + last;
}
